import React, { useReducer } from 'react';
import actionCreator from 'utils/actionCreator';
import { filter } from 'lodash';
import * as notificationClient from 'services/notificationClient';
import { verifyWorker } from 'services/usersClient';
import useUser from 'hooks/useUser/userContext';
import RequestStatus from 'constant/requestStatus';
import {
  respondToApproval,
  updateDocumentAccess,
} from 'services/documentClient';

const NotificationContext = React.createContext();

const GET_NOTIFICATION_LIST = 'get_notification_list';
const GET_NOTIFICATION_LIST_ERROR = 'get_notification_error_list';
const GET_NOTIFICATION_LIST_SUCCESS = 'get_notification_success_list';

const UPDATE_NOTIFICATION = 'update_notification';
const UPDATE_NOTIFICATION_ERROR = 'update_notification_list';
const UPDATE_NOTIFICATION_SUCCESS = 'update_notification_list';

const getNotificationListAction = actionCreator(GET_NOTIFICATION_LIST);
const getNotificationListErrorAction = actionCreator(
  GET_NOTIFICATION_LIST_ERROR,
);
const getNotificationListSuccessAction = actionCreator(
  GET_NOTIFICATION_LIST_SUCCESS,
);

const updateNotificationAction = actionCreator(UPDATE_NOTIFICATION);
const updateNotificationErrorAction = actionCreator(UPDATE_NOTIFICATION_ERROR);
const updateNotificationSuccessAction = actionCreator(
  UPDATE_NOTIFICATION_SUCCESS,
);

const initialState = {
  unseenCount: 0,
  notifications: [],
  loading: false,
  error: null,
};

function notificationReducer(state, action) {
  let unseenCount = 0;
  const { notifications } = state;
  switch (action.type) {
    case GET_NOTIFICATION_LIST:
    case UPDATE_NOTIFICATION:
      return { ...state, error: null, loading: true };
    case GET_NOTIFICATION_LIST_SUCCESS:
      unseenCount = filter(action.payload, (o) => o.seen === false).length;
      return {
        ...state,
        unseenCount,
        notifications: action.payload,
        error: null,
        loading: false,
      };
    case UPDATE_NOTIFICATION_SUCCESS:
      const updatedNotifications = notifications.map((notification) =>
        notification._id === action.payload._id ? action.payload : notification,
      );
      unseenCount = filter(
        updatedNotifications,
        (o) => o.seen === false,
      ).length;

      return {
        ...state,
        unseenCount,
        notifications: updatedNotifications,
        error: null,
        loading: false,
      };
    case GET_NOTIFICATION_LIST_ERROR:
    case UPDATE_NOTIFICATION_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
}
function NotificationProvider({ children }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { refreshUser } = useUser();

  async function getNotificationList() {
    try {
      dispatch(getNotificationListAction());
      const { status, data } = await notificationClient.getAll();
      if (status >= 400) {
        dispatch(getNotificationListErrorAction(data.message));
      } else {
        dispatch(getNotificationListSuccessAction(data.notifications));
      }
    } catch (e) {
      dispatch(getNotificationListErrorAction('Error creating Notification'));
    }
  }

  async function updateNotification(id) {
    try {
      dispatch(updateNotificationAction());
      const { status, data } = await notificationClient.updateNotification(id);
      if (status >= 400) {
        dispatch(updateNotificationErrorAction(data.message));
      } else {
        dispatch(updateNotificationSuccessAction(data.notification));
      }
    } catch (e) {
      dispatch(updateNotificationErrorAction('Error updating Notification'));
    }
  }

  // On success the mark-as-seen response carries the new request state; on
  // failure (typically 409 — answered in another tab) resync from the server.
  async function respondToClientRequest(notificationId, employmentId, status) {
    const { status: httpStatus, data } = await verifyWorker(employmentId, {
      status,
    });

    if (httpStatus >= 400) {
      await getNotificationList();
      throw new Error(data?.message || 'Could not respond to this request');
    }

    await updateNotification(notificationId);
    // The new link unlocks the client's full menu.
    if (status === RequestStatus.ACCEPTED) await refreshUser();
  }

  // Same contract as above: the answer lives on the document, so a failure
  // (409 — already answered elsewhere) resyncs rather than guessing.
  async function respondToDocumentApproval(
    notificationId,
    documentId,
    approvalStatus,
  ) {
    const { status: httpStatus, data } = await respondToApproval(
      documentId,
      approvalStatus,
    );

    if (httpStatus >= 400) {
      await getNotificationList();
      throw new Error(data?.message || 'Could not answer this document');
    }

    await updateNotification(notificationId);
  }

  // WUNA's access request is answered on the access row, same contract as above.
  async function respondToAccessRequest(notificationId, accessId, state) {
    const { status: httpStatus, data } = await updateDocumentAccess(accessId, {
      state,
    });

    if (httpStatus >= 400) {
      await getNotificationList();
      throw new Error(data?.message || 'Could not answer this request');
    }

    await updateNotification(notificationId);
  }

  return (
    <NotificationContext.Provider
      value={{
        state,
        getNotificationList,
        updateNotification,
        respondToClientRequest,
        respondToDocumentApproval,
        respondToAccessRequest,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

function useNotification() {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context;
}

export { useNotification, NotificationProvider, NotificationContext };
